/**
 * Generating elevation (side view) annotations as SVG based on a building.
 * @module
 */
import type { Building } from "building-types/building/buildingTypes";
import { assertIsExactly, assertIsNotUndefined } from "common/utils/assertOfType";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import objectKeyRemove from "common/utils/objectKeyRemove";
import shallowCompare from "common/utils/shallowCompare";
import stableFixedLengthHash from "common/utils/stableFixedLengthHash";
import type { ElevationOcclusionEngine } from "doc/plans/elevationAnnotationCommonParams";
import type { ElevationGenerationContext } from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import { elevationOcclusionSceneBSPOptions, PlanSetBSPProvider } from "lib/house/pdf/planSetBSPProvider";
import { loadWasm } from "lib/house/plans/assets/loadWasm";
import type { ElevationDrawing } from "lib/house/plans/elevationDrawing";
import {
  generateFastElevationAnnotations,
  generateSheetGeometryElevationAutoAnnotations,
  generateSlowElevationAnnotations,
} from "lib/house/plans/generateElevationSheetGeometry";
import type { LazyPropMesh } from "lib/house/plans/perspectiveTypes";
import type { SceneBSP } from "lib/house/plans/SceneBSP";
import type { ElevationAnnotationBuildingState } from "lib/house/render/ElevationAnnotationBuildingState";
import {
  elevationAnnotationBuildingStateEqual,
  elevationAnnotationBuildingStateFromBuilding,
} from "lib/house/render/ElevationAnnotationBuildingState";
import type { ElevationAnnotationProviderParams } from "lib/house/render/elevationAnnotationTypes";
import { loadFonts } from "pdf/fonts";

interface ElevationAutoAnnotationCacheKey {
  paramHash: string;
  buildingState: ElevationAnnotationBuildingState;
}

const cache = new LRUObjectCache<ElevationAutoAnnotationCacheKey, PlanGeometryGeneratorResult>(
  20,
  (a, b) => a.paramHash === b.paramHash && elevationAnnotationBuildingStateEqual(a.buildingState, b.buildingState),
);

const bspProviderCache = new LRUObjectCache<Building, PlanSetBSPProvider>(
  1,
  (a, b) => {
    // The building is stable except for issues, which is created on each run
    return shallowCompare(objectKeyRemove(a ?? {}, "issues"), objectKeyRemove(b, "issues"));
  },
  (bspProvider: PlanSetBSPProvider) => {
    bspProvider.dispose();
  },
);

function getCachedBSPProvider(building: Building): PlanSetBSPProvider {
  if (bspProviderCache.has(building)) {
    const cachedBSPProvider = bspProviderCache.get(building);
    assertIsNotUndefined(cachedBSPProvider);
    return cachedBSPProvider;
  } else {
    const newBSPProvider = new PlanSetBSPProvider(building);
    bspProviderCache.add(building, newBSPProvider);
    return newBSPProvider;
  }
}

// The Rust/WASM HLR path is the production default (see doHLRWasm below). This flag lets us quickly
// switch the occlusion stage back to the deprecated JS BSP path for debugging.
const doJS = false;

// Enables the Rust/WASM Hidden Line Removal engine, the production default. Disable this to fall back
// to the WASM BSP path.
const doHLRWasm = true;

/** The engine selected by the compile-time flags, used when no per-call override is supplied. */
function defaultOcclusionEngine(): ElevationOcclusionEngine {
  if (doHLRWasm) {
    return "hlr-wasm";
  }
  if (doJS) {
    return "js";
  }
  return "wasm";
}

export async function elevationAutoAnnotationPlanGeometry(
  params: Omit<ElevationAnnotationProviderParams, "type">,
): Promise<PlanGeometryGeneratorResult> {
  const { building, ...commonParams } = params;
  const { eyePos } = commonParams;
  const newBuildingState = elevationAnnotationBuildingStateFromBuilding(building, eyePos);
  const hash = await stableFixedLengthHash(commonParams);

  const cacheKey = { paramHash: hash, buildingState: newBuildingState };
  const cached: PlanGeometryGeneratorResult | undefined = cache.get(cacheKey);

  if (cached) {
    return cached;
  }

  await loadFonts();
  const wasm = await loadWasm();

  const bspProvider = getCachedBSPProvider(building);

  assertIsNotUndefined(eyePos);
  assertIsExactly(eyePos[2], 0);

  const engine = commonParams.occlusionEngineOverride ?? defaultOcclusionEngine();
  const sceneBSPOptions = elevationOcclusionSceneBSPOptions(undefined, undefined, commonParams.visibilitySelectors);

  let wasmBuildingBSP: number | undefined;
  if (engine === "wasm") {
    console.time("getSceneBSP (wasm)");
    wasmBuildingBSP = await bspProvider.getWasmSceneBSP(sceneBSPOptions);
    console.timeEnd("getSceneBSP (wasm)");
  }

  let jsBuildingBSP: SceneBSP | undefined;
  if (engine === "js") {
    console.time("getSceneBSP (js)");
    jsBuildingBSP = await bspProvider.getSceneBSP(sceneBSPOptions);
    console.timeEnd("getSceneBSP (js)");
  }

  let hlrFaceGroups: FaceGroupBSP[] | undefined;
  let hlrLazyPropMeshes: LazyPropMesh[] | undefined;
  if (engine === "hlr-wasm") {
    console.time("getSceneViewFaceGroups (hlr)");
    const hlrResult = await bspProvider.getSceneViewFaceGroups(sceneBSPOptions);
    hlrFaceGroups = hlrResult.faceGroups;
    hlrLazyPropMeshes = hlrResult.lazyPropMeshes;
    console.timeEnd("getSceneViewFaceGroups (hlr)");
  }

  const entityTable = bspProvider.getSceneViewEntityTable(sceneBSPOptions);

  const drawnElevation: ElevationDrawing = {
    building,
    wasmBuildingBSP,
    jsBuildingBSP,
    hlrFaceGroups,
    hlrLazyPropMeshes,
    entityTable,
    ...commonParams,
    wasm,
  };

  const annotationStartTime = performance.now();
  const result = await generateSheetGeometryElevationAutoAnnotations(drawnElevation, generateSlowElevationAnnotations);
  const annotationDurationMs = performance.now() - annotationStartTime;

  cache.add(cacheKey, result);

  return {
    ...result,
    timingMs: {
      slowAnnotations: annotationDurationMs,
    },
  };
}

// Exported for use in elevation annotation benchmarks
export function clearElevationAnnotationCache(): void {
  cache.clear();
  bspProviderCache.clear();
}

export async function elevationAutoAnnotationFastGeometryGenerator({
  building,
  ...commonParams
}: ElevationGenerationContext): Promise<PlanGeometryGeneratorResult> {
  const wasm = await loadWasm();
  const drawnElevation: ElevationDrawing = {
    building,
    entityTable: undefined,
    ...commonParams,
    wasm,
  };

  return generateSheetGeometryElevationAutoAnnotations(drawnElevation, generateFastElevationAnnotations);
}
